import { useEffect, useState } from "react";
import Layout from "../../../Frames/Layout";
import { Link, useParams } from "react-router-dom";
import API from "../../../../services/API";
import { Button, Checkbox, IconButton, Stack, Switch, Typography } from "@mui/material";
import { Add, Check, Close, ContentCopy, PersonOff, PersonSearch } from "@mui/icons-material";
import moment from "moment";
import Grid from "../../../Tools/Grid";
import CopyToClipboard from "react-copy-to-clipboard";


export default function List(){

    const {id} = useParams()

    const [session, setSession] = useState(null)

    const [registrations, setRegistrations] = useState(null)

    useEffect(() => {

        API.get(`/dpc/sessions/${id}/registrations`)
            .then(r => setRegistrations(r.data))
        
        API.get(`/dpc/sessions/${id}`)
            .then(r => setSession(r.data))

    }, [id])

    const columns = [
        {
            field: 'lastName',
            headerName: 'Nom',
            width: 120,
            type: 'string',
            valueGetter: p => p.row.registrant.lastName,
        },
        {
            field: 'firstName',
            headerName: 'Prénom',
            width: 150,
            type: 'string',
            valueGetter: p => p.row.registrant.firstName,
        },
        {
            field: 'rpps',
            headerName: 'RPPS',
            width: 140,
            type: 'string',
            valueGetter: p => p.row.registrant.rpps,
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 200,
            type: 'string',
            valueGetter: p => p.row.registrant.email,
        },
        {
            field: 'linked',
            headerName: 'Affectée',
            width: 80,
            type: 'boolean',
            valueGetter: p => p.row.enrollment?.id ? true : false,
            renderCell: p => 
                p.value ? <Check color="success" /> : <Close color="error" />
        },
        {
            field: 'user',
            headerName: 'Fiche client',
            width: 100,
            type: 'string',
            valueGetter: p => p.row.user?.id,
            renderCell: p => 
                <IconButton 
                    disabled={!p.value}
                    component={Link}
                    to={p.value ? `/crm/persons/${p.value}` : ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    color={p.value ? 'primary' : 'error'}
                >
                    {p.value ? <PersonSearch /> : <PersonOff />}
                </IconButton>
        },
        {
            field: 'canceled',
            headerName: 'Désinscrit',
            width: 100,
            type: 'boolean',
            valueGetter: p => p.row.canceled,
            renderCell: p => 
                <Switch 
                    checked={p.value} 
                    color="warning"
                    onClick={() => handleCancel(p.row)}
                    size="small"
                />
        },
        {
            field: 'signature',
            headerName: 'Attestation',
            width: 100,
            type: 'boolean',
            valueGetter: p => p.row.signature?.signed,
            renderCell: p => 
                p.value ? <Check color="success" /> : <Close color="error" />
        },
        {
            field: 'signatureUrl',
            headerName: 'Lien Signature',
            width: 120,
            type: 'string',
            valueGetter: p => p.row.signature?.url,
            renderCell: p => 
                <CopyToClipboard text={p.value}>
                    <IconButton size="small">
                        <ContentCopy />
                    </IconButton>
                </CopyToClipboard>
        },
        {
            field: 'completion',
            headerName: 'Complétion',
            width: 100,
            type: 'number',
            valueGetter: p => p.row.completion,
            renderCell: p => 
                <span>{p.value.toLocaleString()}<small>%</small></span>
        },
        {
            field: 'completed',
            headerName: 'Terminée',
            width: 90,
            type: 'boolean',
            valueGetter: p => p.row.completion === 100,
            renderCell: p => p.row.enrollment &&
                <Checkbox 
                    checked={p.value}
                    disabled={p.value}
                    size="small"
                    onClick={() => handleComplete(p.row)}
                />
        },
        {
            field: 'contribution',
            headerName: 'Montant',
            width: 80,
            type: 'number',
            valueGetter: p => p.row.contribution,
            renderCell: p => 
                <span>{p.value.toLocaleString()}<small>€</small></span>
        },
        {
            field: 'createdAt',
            headerName: 'Inscrit le',
            width: 100,
            type: 'date',
            valueGetter: p => new Date(p.row.createdAt),
            renderCell: p => moment(p.value).format('DD/MM/YYYY')
        },
    ];

    const update = registrationCopy => {
        let newRegistrations = [...registrations];
        const index = registrations.findIndex(r => r.id === registrationCopy.id);
        newRegistrations[index] = registrationCopy;
        setRegistrations(newRegistrations);
    }

    const handleCancel = reg => {

        // Confirm enrollment delete
        if(!reg.canceled && reg.enrollment){

            const confirm = window.confirm(cancelMessage);

            if(!confirm) return false;
        }

        // Optimistic registration update
        const oldRegistrations = [...registrations];

        update({...reg,
            canceled: !reg.canceled,
            completion: 0,
            enrollment: null
        });

        // API call to toggle registration and delete enrollment if needed
        cancelRegistration(reg)
            .catch(_ => setRegistrations(oldRegistrations))
    }

    const handleComplete = reg => {

        if(!reg.enrollment || reg.completion === 100) return false;

        // Confirm message
        const confirm = window.confirm(completeMessage);

        if(!confirm) return false;

        // Optimistic registration update
        const oldRegistrations = [...registrations];

        update({...reg, completion: 100});

        // API call to set the enrollment completion to 100%
        API.put(`/crm/enrollments/${reg.enrollment.id}/complete`)
            .catch(_ => setRegistrations(oldRegistrations))
    }

    return (
        <Layout title="Inscriptions DPC" tab="dpc" menu="admin" back={true}>

            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{mb: 1}}>

                <Typography>
                    <b>Session {session?.number} |</b> {session?.action?.title}
                </Typography>

                <Button 
                    startIcon={<Add/>} 
                    component={Link} 
                    to={`/admin/dpc/sessions/${id}/registrations/new`}
                    variant="contained"
                >
                    Inscrire
                </Button>

            </Stack>

            <Grid
                id={`registrations${id}Grid`}
                columns={columns}
                rows={registrations}
                toolbar
            />

        </Layout>
    )
}


async function cancelRegistration(reg){

    try {

        if(!reg.canceled && reg.enrollment){

            return await API.delete(`/crm/enrollments/${reg.enrollment.id}`);
        
        }else{

            return await API.put(`/dpc/registrations/${reg.id}/cancel`);
        }

    } catch(error) {

        throw new Error("Update failed");
    }
}


const cancelMessage = "Attention : si vous désinscrivez l'apprenant, la formation sera supprimée de son compte et sa complétion sera perdue. Voulez-vous continuer ?";

const completeMessage = "Voulez-vous marquer cette formation comme terminée ? Cette opération est irréversible.";